import {
  createElement,
  Fragment,
  useState,
  useRef,
  ComponentProps,
  useEffect,
  useCallback,
} from 'react';
import { AvatarSelectorProps } from '.';
import { OmitStrict } from '../../utility-types';
import { Edit } from '@sas/react-svg';
import { BaseImageSelector } from '../ImageSelector/__internal__/BaseImageSelector';
import { Icon } from '../Icon';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { useAvatarSelectorA11yInfo } from './__internal__/useAvatarSelectorA11yInfo';
import { Avatar } from '../Avatar';
import { getLogger } from '@sas/nova-core/logging';
import { withLabels } from '../withLabels';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import {
  useOnAvatarUpdate,
  usePublishAvatarUpdate,
} from '../Avatar/__internal__/useOnAvatarUpdate';
import { getAvatarSelectorDataProvider } from './__internal__/AvatarSelectorDataProvider';
import cn from './AvatarSelector.css.json';
import './AvatarSelector.css';

const logger = getLogger('AvatarSelector');

export const AvatarSelector = ({
  imageSelectorProps,
  dataProvider = getAvatarSelectorDataProvider(),
  ...props
}: AvatarSelectorProps) => {
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const [isDefaultAvatar, setIsDefaultAvatar] = useState(true);
  const { tooltip, ariaLabel } = useAvatarSelectorA11yInfo({ ...props, dataProvider });
  const [fetchedTime, setFetchedTime] = useState<number>();
  const fetchCounter = useRef(0);
  const publishAvatarUpdate = usePublishAvatarUpdate(dataProvider, props.userId, props.type);

  useEffect(() => {
    return () => {
      // Increment the fetchCounter to discard the fetch response after unmounting
      fetchCounter.current += 1;
    };
  }, []);

  const updateIsDefaultAvatar = useCallback(() => {
    const fetchCount = ++fetchCounter.current;
    dataProvider
      .getAvatar?.(props.userId, props.type)
      .then((src) => {
        if (fetchCounter.current > fetchCount) return;
        setIsDefaultAvatar(src.length === 0);
      })
      .catch(() => {
        if (fetchCounter.current > fetchCount) return;
        setIsDefaultAvatar(true);
        logger.error(
          // eslint-disable-next-line @sas/i18n/jsx-to-localize
          `Failed to fetch the avatar details info for id:${props.userId} and type: ${props.type}`
        );
      })
      .finally(() => {
        if (fetchCounter.current > fetchCount) return;
        setFetchedTime(Date.now());
      });
  }, [dataProvider, props.userId, props.type]);

  useOnAvatarUpdate(dataProvider, updateIsDefaultAvatar, props.userId, props.type, fetchedTime);

  useEffect(updateIsDefaultAvatar, [updateIsDefaultAvatar]);

  const onRemoveAvatar = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      dataProvider
        .deleteAvatar(props.userId, props.type)
        .then(() => {
          publishAvatarUpdate();
          resolve();
        })
        .catch(reject);
    });
  };

  const onSaveAvatar = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      dataProvider
        .saveAvatar(src, props.userId, props.type)
        .then(() => {
          props.onSaveComplete?.({ status: 'success' });
          publishAvatarUpdate();
          resolve();
        })
        .catch(() => {
          props.onSaveComplete?.({ status: 'failure' });
          reject();
        });
    });
  };

  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(
    {
      'spacing-m': dt.spacing.core.m,
      'edit-icon-bd': dt.c.colorPrimary,
      'edit-icon-bg': dt.c.colorTextDefault,
    },
    'avatar-selector'
  );

  return (
    <Fragment>
      <BaseImageSelector
        ellipse
        // Set the src to 'dummy string' when avatar is not the default one to enable the "Remove image" menu item.
        // Since it won't be shown to the end-user so no need to localize
        src={isDefaultAvatar ? undefined : 'dummy string'}
        imageSize={{}}
        renderImageContainer={({ ref, onPress, onFocus }) => (
          <div
            className={cn.avatarContainer}
            ref={ref}
            onFocus={() => {
              if (avatarRef.current) {
                onFocus(avatarRef.current);
              }
            }}
            style={tokens}
          >
            <Avatar
              {...props}
              dataProvider={dataProvider}
              ref={(element) => {
                avatarRef.current = element;
                setRef(props.avatarRef, element);
              }}
              useAsButton
              onPress={onPress}
              className={cn.avatar}
              size={props.size ?? 'extraLarge'}
              tooltip={tooltip}
              ariaLabel={ariaLabel}
              testId={postfixTestId(props.testId, 'avatar')}
            />
            <div className={cn.editIcon} onClick={onPress}>
              <Icon
                icon={Edit}
                tokens={() => {
                  return { fg: dt.c.colorTextInverse };
                }}
                tooltip={tooltip}
              />
            </div>
          </div>
        )}
        onSaveImage={onSaveAvatar}
        onRemoveImage={onRemoveAvatar}
        testId={postfixTestId(props.testId, 'imageSelector')}
        {...imageSelectorProps}
      />
    </Fragment>
  );
};

const WithLabels = withLabels(AvatarSelector);
export const AvatarSelectorWithLabels = (
  props: OmitStrict<ComponentProps<typeof WithLabels>, 'suppressHtmlFor' | 'labelingAttr'>
) => {
  const dataProvider = props.dataProvider ?? getAvatarSelectorDataProvider();
  // withLabels can only concatenate the label with customized the ariaLabel, so feed the default ariaLabel as
  // customized one to make sure the default ariaLabel is also concatenated
  const { ariaLabel } = useAvatarSelectorA11yInfo({ ...props, dataProvider });
  return <WithLabels {...props} dataProvider={dataProvider} ariaLabel={ariaLabel} />;
};
